import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';

import { Button, Card } from '../components/index.ts';
import { db } from '../db/db.ts';
import { isIos, isStandalone, shouldOfferInstall } from './install.ts';

/**
 * The offer to install to the home screen.
 *
 * A dismissible card, never a modal: it appears in Settings whenever it
 * applies, and once in the entry list after a trainee's fifth entry. iOS has no
 * `beforeinstallprompt`, so there it is instructions rather than a button —
 * which is most of this app's users, so the instructions are the main path and
 * not an afterthought.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISSED_KEY = 'installPromptDismissed';

export function InstallCard({ force = false }: { force?: boolean }) {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [standalone] = useState(() => isStandalone());

  useEffect(() => {
    const capture = (event: Event): void => {
      // Keep it so the browser's own prompt can be raised from our button
      // rather than whenever Chrome feels like it.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', capture);
    return () => window.removeEventListener('beforeinstallprompt', capture);
  }, []);

  const state = useLiveQuery(async () => {
    const dismissed = await db.meta.get(DISMISSED_KEY);
    const entryCount = (await db.cases.count()) + (await db.procedures.count());

    return { dismissed: dismissed?.value === true, entryCount };
  }, []);

  if (state === undefined) return null;
  if (
    !shouldOfferInstall({
      standalone,
      dismissed: state.dismissed,
      entryCount: state.entryCount,
      force,
    })
  ) {
    return null;
  }

  async function dismiss(): Promise<void> {
    await db.meta.put({ key: DISMISSED_KEY, value: true });
  }

  async function install(): Promise<void> {
    if (deferred === null) return;

    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }

  return (
    <Card title="Add to your home screen">
      <p className="text-sm text-text">
        Installed, ICULogbook opens like an app — full screen, straight to the log form, and it
        works with no signal.
      </p>
      <p className="pt-2 text-sm text-muted">
        On an iPhone it also stops Safari clearing your entries after a week without a visit, which
        is the one way this app can lose work that has not backed up yet.
      </p>

      {deferred !== null ? (
        <div className="flex flex-wrap gap-2 pt-3">
          <Button variant="primary" onClick={() => void install()}>
            Install
          </Button>
          <Button variant="ghost" onClick={() => void dismiss()}>
            Not now
          </Button>
        </div>
      ) : (
        <>
          <ol className="flex list-decimal flex-col gap-2 pt-3 pl-5 text-sm text-muted">
            {isIos() ? (
              <>
                <li>
                  Tap the <span className="font-medium text-text">Share</span> button at the bottom
                  of Safari — the square with an arrow out of it.
                </li>
                <li>
                  Scroll down and tap{' '}
                  <span className="font-medium text-text">Add to Home Screen</span>.
                </li>
                <li>
                  Tap <span className="font-medium text-text">Add</span>. Open ICULogbook from the
                  new icon from then on.
                </li>
              </>
            ) : (
              <>
                <li>Open the browser menu — the three dots at the top or bottom right.</li>
                <li>
                  Tap <span className="font-medium text-text">Install app</span> or{' '}
                  <span className="font-medium text-text">Add to Home screen</span>.
                </li>
              </>
            )}
          </ol>

          <div className="pt-3">
            <Button variant="ghost" onClick={() => void dismiss()}>
              Not now
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
